import { useState } from "react";

import EditMentionModal from "@/containers/EditMentionModal";
import { useMentionCard } from "@/containers/MentionCard/context";
import ExternalLink from "@/containers/MentionCard/partials/Body/partials/ExternalLink";
import Actions from "@/containers/MentionCard/partials/Mention/Actions";
import RemoveMention from "@/containers/MentionCard/partials/Mention/RemoveMention";
import Similar from "@/containers/MentionCard/partials/Mention/Similar";
import Topic from "@/containers/MentionCard/partials/Mention/Topic";
import Translate from "@/containers/MentionCard/partials/Translate";
import { useI18n } from "@/contexts/i18nContext";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import { AudioMentionV4 } from "@/types/MentionCard";
import { cx } from "@/utils";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import UndoRemovedMention from "@components/UndoRemovedMention";

import styles from "./Mention.module.css";

const Mention: React.FunctionComponent<{
  hideActions?: boolean;
  className?: string;
  mention: AudioMentionV4;
}> = ({ className, mention, hideActions: forceHideActions }) => {
  const { language } = useI18n();
  const { updateMention } = useMentionCards();
  const {
    hideActions,
    hideMetadata,
    hideSimilar,
    removedMentionIds,
    mentions,
    channel,
    topic_names,
    episode: { id: episode_uuid },
  } = useMentionCard();
  const [editing, setEditing] = useState(false);

  const isRemoved = removedMentionIds.includes(mention.id);
  const allMentionsRemoved = removedMentionIds.length === mentions.length;

  const channel_language = channel.language_guess || channel.language;
  const channelNotInUserLanguage = channel_language !== language;

  return (
    <>
      <div
        className={cx(
          styles.root,
          "mention",
          { marked: mention.marked_irrelevant_at },
          className
        )}
        data-mention-id={mention.id}
      >
        <div>
          {isRemoved && !allMentionsRemoved ? (
            <UndoRemovedMention currentId={mention.id} />
          ) : (
            <div className={styles.actions}>
              {!hideMetadata && <Topic topic_names={topic_names} />}

              {!forceHideActions &&
                !hideActions &&
                channelNotInUserLanguage && <Translate mentions={[mention]} />}
              <ExternalLink />
              {!forceHideActions && !hideActions && (
                <AccessControl level={ACCESS_LEVELS.USER}>
                  <RemoveMention currentId={mention.id} />
                  <Actions mention={mention} setEditing={setEditing} />
                </AccessControl>
              )}
            </div>
          )}

          {!hideSimilar && mention.similar_mentions?.length > 0 && (
            <Similar
              mentions={mention.similar_mentions}
              episode_pk={episode_uuid}
            />
          )}
        </div>
      </div>
      <EditMentionModal
        show={editing}
        onHide={() => setEditing(false)}
        mention={mention}
        updateMention={updateMention}
      />
    </>
  );
};

export default Mention;
